export type ClientIdentificationType = 'cpf' | 'cei' | 'cnpj' | 'unknown'

/** Mantém apenas dígitos da identificação (máx. 14). */
export function normalizeIdentificationDigits(value: string): string {
  return value.replace(/\D/g, '').slice(0, 14)
}

/** Infere o tipo pelo tamanho: 11 CPF, 12 CEI, 14 CNPJ. */
export function detectIdentificationType(
  value: string
): ClientIdentificationType {
  const digits = normalizeIdentificationDigits(value)
  if (digits.length === 11) return 'cpf'
  if (digits.length === 12) return 'cei'
  if (digits.length === 14) return 'cnpj'
  return 'unknown'
}

export function identificationTypeLabel(type: ClientIdentificationType): string {
  switch (type) {
    case 'cpf':
      return 'CPF'
    case 'cei':
      return 'CEI'
    case 'cnpj':
      return 'CNPJ'
    default:
      return 'Identificação'
  }
}

function formatCpfMask(digits: string): string {
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`
  if (digits.length <= 9) {
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`
  }
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`
}

/** Máscara CEI: 00.000.00000/00 */
function formatCeiMask(digits: string): string {
  if (digits.length <= 2) return digits
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`
  if (digits.length <= 10) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`
  }
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 10)}/${digits.slice(10)}`
}

function formatCnpjMaskLocal(digits: string): string {
  if (digits.length <= 2) return digits
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`
  if (digits.length <= 8) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`
  }
  if (digits.length <= 12) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`
  }
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`
}

/** Formata identificação conforme o tipo inferido pelo tamanho. */
export function formatIdentificationMask(value: string): string {
  const digits = normalizeIdentificationDigits(value)
  if (!digits) return ''

  const type = detectIdentificationType(digits)
  switch (type) {
    case 'cpf':
      return formatCpfMask(digits)
    case 'cei':
      return formatCeiMask(digits)
    case 'cnpj':
      return formatCnpjMaskLocal(digits)
    default:
      return digits
  }
}

/** Label + valor formatado (ex.: "CNPJ 12.345.678/0001-90"). */
export function formatIdentificationDisplay(value: string): string {
  const digits = normalizeIdentificationDigits(value)
  if (!digits) return ''
  const type = detectIdentificationType(digits)
  const label = identificationTypeLabel(type)
  const formatted = formatIdentificationMask(digits)
  return `${label} ${formatted}`
}
